import { getClassLevel } from './classIdentity.mjs';
export const getRageLevel = character => getClassLevel(character, 'barbarian');
const normalize = value => String(value ?? '').trim().toLowerCase();
export function hasHeavyArmor(character) {
  return Object.values(character?.equipment || {}).some(item => {
    if (Array.isArray(item)) return normalize(item[0]) === 'heavy' || /heavy[\s_-]*armor/.test(normalize(item[0]));
    if (!item || typeof item !== 'object') return false;
    const source = normalize(item.source || item.__source);
    const category = normalize(item.armorCategory ?? item.armor_category ?? item.armorType ?? item.armor?.category ?? item.category);
    const type = normalize(item.type);
    if (source === 'weapon' || /weapon|melee|ranged/.test(category)) return false;
    return category === 'heavy' || category.includes('heavy armor') || type === 'heavy armor' ||
      (source === 'armor' && ['plate','splint','chain mail','ring mail'].includes(normalize(item.name).replace(/-/g, ' ')));
  });
}
export function rageProfile(character = {}) {
  const level = getRageLevel(character);
  const maximum = !level ? 0 : level >= 17 ? 6 : level >= 12 ? 5 : level >= 6 ? 4 : level >= 3 ? 3 : 2;
  const raw = character.classState?.barbarian?.rage ?? character.barbarian?.rage ?? {};
  const growth = Number.isInteger(raw.maximum) ? Math.max(0, maximum - raw.maximum) : 0;
  const current = Number.isFinite(Number(raw.current)) ? Math.max(0, Math.min(maximum, Math.floor(Number(raw.current)) + growth)) : maximum;
  return { level, maximum, current, active: Boolean(level && raw.active), revision: Math.max(0, Math.floor(Number(raw.revision) || 0)), raw };
}
export function mergeRage(character, rage) {
  if (!rage || !Number.isInteger(rage.revision) || rage.revision < rageProfile(character).revision) return character;
  return { ...character, classState: { ...character.classState, barbarian: { ...character.classState?.barbarian, rage } } };
}
// Incapacitated and the conditions that include it (2024).
const INCAPACITATING = new Set(['incapacitated', 'unconscious', 'paralyzed', 'stunned', 'petrified']);
const incapacitatingEffect = (character, combat) => {
  const ids = [character._id, character.characterId, character.combatantId].filter(Boolean).map(String);
  return (combat?.activeEffects || []).find(e => ids.includes(String(e.targetCombatantId)) && INCAPACITATING.has(normalize(e.definitionId).replace(/^condition:/, '')));
};
export function isIncapacitated(character = {}, combat = {}) {
  if (Number(character.tempHealth ?? character.health) <= 0 || character.deathState?.isDead || character.deathState?.isDying) return true;
  const local = [...(character.conditions || []), ...(character.statusConditions || [])].some(c => INCAPACITATING.has(normalize(c?.name ?? c?.label ?? c?.id ?? c).replace(/^condition:/, '')));
  return local || Boolean(incapacitatingEffect(character, combat));
}
/**
 * Why an Incapacitated creature can't act, for refusals: "Incapacitated (Hideous Laughter)", "Stunned", "at 0 Hit Points".
 * Null when it can act.
 */
export function incapacitationReason(character = {}, combat = {}) {
  if (!isIncapacitated(character, combat)) return null;
  const effect = incapacitatingEffect(character, combat);
  if (!effect) return Number(character.tempHealth ?? character.health) <= 0 || character.deathState?.isDead || character.deathState?.isDying ? 'at 0 Hit Points' : 'Incapacitated';
  const condition = normalize(effect.definitionId).replace(/^condition:/, '');
  const source = String(effect.name || '').split(' · ')[1];
  return `${condition.charAt(0).toUpperCase()}${condition.slice(1)}${source ? ` (${source})` : ''}`;
}
// Persistent Rage (Barbarian 15): Rage lasts its full 10 minutes without being kept going, and ends early only if the
// Barbarian is Unconscious or dons Heavy armor.
export const PERSISTENT_RAGE_LEVEL = 15;
export const hasPersistentRage = character => getRageLevel(character) >= PERSISTENT_RAGE_LEVEL;
export function isUnconscious(character = {}, combat = {}) {
  if (Number(character.tempHealth ?? character.health) <= 0 || character.deathState?.isDead || character.deathState?.isDying) return true;
  const ids = [character._id, character.characterId, character.combatantId].filter(Boolean).map(String);
  const local = [...(character.conditions || []), ...(character.statusConditions || [])].some(c => normalize(c?.name ?? c?.label ?? c?.id ?? c).replace(/^condition:/, '') === 'unconscious');
  return local || (combat.activeEffects || []).some(e => ids.includes(String(e.targetCombatantId)) && normalize(e.definitionId) === 'condition:unconscious');
}
export function rageStopReason(character, combat = {}) {
  if (hasHeavyArmor(character)) return 'heavy-armor';
  if (hasPersistentRage(character)) return isUnconscious(character, combat) ? 'unconscious' : null;
  return isIncapacitated(character, combat) ? 'incapacitated' : null;
}
export function rageRestState(character, type) {
  const p = rageProfile(character);
  return { ...p.raw, active: false, maximum: p.maximum, current: type === 'long' ? p.maximum : Math.min(p.maximum, p.current + 1), revision: p.revision + 1, endedReason: 'rest', ownTurnsRemaining: 0, outsideSecondsRemaining: 0,
    // Relentless Rage's DC returns to 10 on any rest; Persistent Rage's Initiative refill returns on a Long Rest.
    relentlessUses: 0, relentless: null,
    ...(type === 'long' ? { persistentRefillUsed: false } : {}) };
}
export function advanceRage(character, previous = {}, next = previous, seconds = 0) {
  const p = rageProfile(character); if (!p.active) return null;
  // A pending Relentless Rage save holds the Rage as it is until the player answers it.
  if (p.raw.relentless?.status === 'pending') return null;
  const inCombat = Number.isInteger(next.activeTurn) && Boolean(next.participants?.[next.activeTurn]);
  const ids = [character._id, character.characterId, character.combatantId].filter(Boolean).map(String);
  const changedTurn = previous.turnSequence !== next.turnSequence;
  const ownTurnEnded = changedTurn && ids.includes(String(previous.participants?.[previous.activeTurn]?.characterId || previous.participants?.[previous.activeTurn]?.enemyId || ''));
  const roundSeconds = changedTurn ? Math.max(0, (Number(next.round) || 1) - (Number(previous.round) || 1)) * 6 : 0;
  const remaining = Math.max(0, (Number.isFinite(p.raw.secondsRemaining) ? p.raw.secondsRemaining : 600) - roundSeconds - seconds);
  const ownTurnsRemaining = Math.max(0, (Number.isInteger(p.raw.ownTurnsRemaining) ? p.raw.ownTurnsRemaining : 2) - (ownTurnEnded ? 1 : 0));
  const outsideSecondsRemaining = Math.max(0, (Number.isFinite(p.raw.outsideSecondsRemaining) ? p.raw.outsideSecondsRemaining : 12) - (!inCombat ? seconds : 0));
  const persistent = hasPersistentRage(character);
  const reason = rageStopReason(character, next) || (remaining === 0 ? 'ten-minute-limit' : !persistent && (ownTurnsRemaining === 0 || (!inCombat && outsideSecondsRemaining === 0)) ? 'expired' : null);
  // A Rage that has been in combat ends when the combat does: with no more turns, nothing can extend it. (`fought`
  // marks that; an older Rage started on a combat turn has a startedTurnSequence.) A Rage started before initiative
  // keeps its short out-of-combat window instead.
  const fought = Boolean(p.raw.fought) || p.raw.startedTurnSequence != null || inCombat;
  const ended = reason || (!persistent && !inCombat && fought ? 'combat-ended' : null);
  const migrated = !p.raw.activationId;
  if (!ended && !migrated && Boolean(p.raw.fought) === fought && p.raw.maximum === p.maximum && p.raw.current === p.current && remaining === p.raw.secondsRemaining && ownTurnsRemaining === p.raw.ownTurnsRemaining && outsideSecondsRemaining === p.raw.outsideSecondsRemaining) return null;
  return { ...p.raw, active: !ended, current: p.current, maximum: p.maximum, revision: p.revision + 1, activationId: p.raw.activationId || 'legacy', secondsRemaining: remaining, ownTurnsRemaining, outsideSecondsRemaining, fought, ...(ended ? { endedReason: ended } : {}) };
}

// Damage resistance is a benefit of a valid, currently active Rage. Reuse the
// lifecycle checks so stale UI snapshots cannot keep benefits after a stop cause.
export function hasRageDamageResistance(character, type, combat = {}, targetId) {
  if (!['bludgeoning', 'piercing', 'slashing'].includes(String(type || '').trim().toLowerCase())) return false;
  return isRageEffective(character, combat, targetId);
}
export function isRageEffective(character, combat = {}, targetId) {
  const p = rageProfile(character);
  const deadlines = hasPersistentRage(character) ? ['secondsRemaining'] : ['secondsRemaining', 'ownTurnsRemaining', 'outsideSecondsRemaining'];
  if (!p.active || deadlines.some(key => p.raw[key] === 0)) return false;
  const recipient = targetId ? { ...character, combatantId: targetId } : character;
  return !rageStopReason(recipient, combat);
}

// A Beast form also stops spellcasting, until Beast Spells (Druid 18). Read from the character because this runs on the
// server's ESM side, which doesn't load beastForm.js through this module.
export const rageSpellcastingBlockReason = character => {
  if (rageProfile(character).active) return 'Cannot cast spells while Rage is active.';
  const form = character?.classState?.druid?.wildShape?.form;
  return form && !form.beastSpells ? 'You can’t cast spells in Beast form.' : '';
};
